import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useId, useRef, useState, type ReactElement } from 'react';
import type { Line } from '@chatapp-inbox/shared';
import { isApiError, setLineEnabled } from '../../api';
import { lineName } from '../conversations';
import { StatusRegion } from '../details/StatusRegion';
import { useStatusMessage } from '../details/use-status-message';
import { adminErrorMessage } from './error-message';
import { LineRegistration } from './LineRegistration';
import { LineRename } from './LineRename';
import { connectionKey } from './keys';
import { replyWindowText } from './lines-text';
import { Switch } from './Switch';
import { applyLine } from './use-connection';
import forms from './forms.module.css';
import section from './Section.module.css';
import styles from './LinesSection.module.css';

/**
 * Um número da conexão, com o interruptor ligar/desligar. O interruptor mostra o estado NOVO assim
 * que é acionado (otimista); se o servidor recusar, volta ao que era e a linha avisa o motivo. Cada
 * linha tem a sua própria mutação, então ligar um número não trava os outros, e enquanto a dela
 * está em andamento um segundo clique é ignorado (não há dois PATCH concorrentes para o mesmo número).
 *
 * O nome e o telefone são texto de terceiros (vêm do serviço de mensagens): `dir="auto"` e quebra
 * de palavras longas.
 */
export function LineRow({
  line,
  canRegister = false,
}: {
  line: Line;
  /** O serviço registra números (`ConnectionState.canRegisterLines`): mostra "Situação do número". */
  canRegister?: boolean;
}): ReactElement {
  const queryClient = useQueryClient();
  const windowId = useId();
  const status = useStatusMessage();
  const [error, setError] = useState<string | null>(null);
  // Guarda síncrona: `isPending` só vale depois do re-render, e dois cliques podem chegar antes.
  const inFlight = useRef(false);
  const name = lineName(line);

  const toggle = useMutation({
    mutationFn: (enabled: boolean) => setLineEnabled(line.id, enabled),
    onSuccess: (saved) => {
      applyLine(queryClient, saved);
      status.announce(`Número ${lineName(saved)} ${saved.enabled ? 'ligado' : 'desligado'}.`);
    },
    onError: (failure, enabled) => {
      setError(
        `Não foi possível ${enabled ? 'ligar' : 'desligar'} o número ${name}. ${adminErrorMessage(failure, 'Tente de novo.')}`,
      );
      // 404: o número deixou de existir (a lista está velha).
      if (isApiError(failure) && failure.status === 404) {
        void queryClient.invalidateQueries({ queryKey: connectionKey });
      }
    },
    onSettled: () => {
      inFlight.current = false;
    },
  });

  // Enquanto o PATCH não volta, vale o que a pessoa pediu; se falhar, `isPending` cai e a linha
  // volta a mostrar o estado do servidor (o cache nunca foi tocado).
  const shown = toggle.isPending ? (toggle.variables ?? line.enabled) : line.enabled;
  const phone = line.phone?.trim() ?? '';
  // `null` = o número não tem regra de janela (nada a dizer).
  const windowHours = typeof line.replyWindowHours === 'number' ? line.replyWindowHours : null;

  return (
    <li className={styles.row}>
      <div className={styles.main}>
        <div className={styles.identity}>
          <p dir="auto" className={styles.name}>
            {name}
          </p>
          {phone && phone !== name && (
            <p dir="auto" className={styles.phone}>
              {phone}
            </p>
          )}
          {/* Prazo da janela de resposta do número: descrição do interruptor, não do nome. */}
          {windowHours !== null && (
            <p id={windowId} className={styles.window}>
              {replyWindowText(windowHours)}
            </p>
          )}
        </div>
        <div className={styles.control}>
          {/* A situação em PALAVRAS; o leitor de tela já a recebe do interruptor (`aria-checked`). */}
          <span className={shown ? section.badgeOn : section.badgeOff} aria-hidden="true">
            {shown ? 'Ligado' : 'Desligado'}
          </span>
          <Switch
            checked={shown}
            label={`Usar o número ${name}`}
            busy={toggle.isPending}
            describedBy={windowHours !== null ? windowId : undefined}
            onToggle={() => {
              if (inFlight.current) return;
              inFlight.current = true;
              setError(null);
              status.announce('');
              toggle.mutate(!line.enabled);
            }}
          />
        </div>
      </div>
      <StatusRegion message={status.message} />
      <LineRename line={line} />
      {canRegister && <LineRegistration line={line} />}
      {error && (
        <p role="alert" className={forms.alert}>
          {error}
        </p>
      )}
    </li>
  );
}
